import QueryTags from '../../types/QueryTags'
import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { GetLoanProfileResponse, GetLoansApplicationsResponse, GetLoansResponse, InitiateLoanResponse, BaseResponse, ViewApplicationStage, LoanContractResponse, InsuranceContractResponse, UploadFileResponse, CheckLoanEligibilityResponse, AllowedLoans } from './types/response'
import { AcceptLoanOffer, CheckLoanEligibilityRequest, InitiateLoanRequest, PersonalLoanEligibility, ProvideAdditionalInfoRequest, RejectLoanOffer, SaveHealthInfoRequest, SignLoanContractRequest, UploadFileRequest } from './types/request'
import { LoanAccountResponse, LoanBalanceResponse, LoanPaymentHistoryResponse, LoanRepayRequest, LoanRepayResponse, LoanScheduleResponse } from '../accounts/types'
import { Loan } from 'types'


const loanOriginationPath = '/loan-origination'
const loanManagementPath = '/loan-management'
const fileManagerPath = '/bkimageservice/fileManager'

const loanEndpoints = (builder: EndpointBuilder<
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, QueryTags, 'data'>) =>
  ({
    getLoanAccounts: builder.query<LoanAccountResponse[], string>({
      query: (customerNumber) => {
        return `${loanManagementPath}/loans/${customerNumber}`
      },
      providesTags: ['loanAccounts'],
    }),
    getLoanAccountsCbs: builder.query<LoanAccountResponse[], string>({
      query: (customerNumber) => {
        return `${loanManagementPath}/loans/refresh/${customerNumber}`
      },
      providesTags: ['loanAccounts'],
    }),
    getLoanSchedule: builder.query<LoanScheduleResponse[], string>({
      query: (id) => `${loanManagementPath}/loans/${id}/schedule`
    }),
    getLoanBalance: builder.query<LoanBalanceResponse, string>({
      query: (id) => `${loanManagementPath}/loans/${id}/balance`
    }),
    getLoanAccount: builder.query<LoanAccountResponse, string>({
      query: (loanId) => {
        return `${loanManagementPath}/loans/${loanId}`
      }
    }),
    getLoanRepaymentHistory: builder.query<LoanPaymentHistoryResponse[], string>({
      query: (customerNumber) => {
        return `${loanManagementPath}/loans/repay-history/customer/${customerNumber}`
      }
    }),
    initiateLoanRepay: builder.mutation<LoanRepayResponse, LoanRepayRequest>({
      query: ({ loanId, ...body }) => {
        return ({
          url: `${loanManagementPath}/loans/${loanId}/repay`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanAccounts'],
    }),
    getLoanProfile: builder.query<GetLoanProfileResponse, { cbsId: string, }>({
      query: ({ cbsId }) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/${cbsId}`,
          method: 'GET',
        })
      }
    }),
    getLoanProfileUpdated: builder.query<GetLoanProfileResponse, { cbsId: string, }>({
      query: ({ cbsId }) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/${cbsId}/updated`,
          method: 'GET',
        })
      }
    }),
    getLoanProfileDecrypted: builder.query<GetLoanProfileResponse, { cbsId: string, }>({
      query: ({ cbsId }) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/${cbsId}/decrypted`,
          method: 'GET',
        })
      }
    }),
    getLoans: builder.query<Loan[], { includeStaff?: boolean, }>({
      query: ({ includeStaff }) => {
        return ({
          url: `${loanOriginationPath}/loans`,
          method: 'GET',
          params: { includeStaff }
        })
      }
    }),
    initiateLoan: builder.mutation<InitiateLoanResponse, InitiateLoanRequest>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/loans/apply`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    getLoansApplicationsList: builder.query<GetLoansApplicationsResponse[], { cbsId: string, }>({
      query: ({ cbsId }) => {
        return ({
          url: `${loanOriginationPath}/loans/application/${cbsId}`,
          method: 'GET',
        })
      },
      providesTags: ['loanApplications']
    }),
    getHealthQuestions: builder.query<GetLoanProfileResponse, void>({
      query: () => {
        return ({
          url: `${loanOriginationPath}/customer-loans/health-questions`,
          method: 'GET',
        })
      }
    }),
    saveHealthInfo: builder.mutation<BaseResponse, SaveHealthInfoRequest>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/health-info`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    acceptLoanOffer: builder.mutation<BaseResponse, AcceptLoanOffer>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/accept-offer`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    rejectLoanOffer: builder.mutation<BaseResponse, RejectLoanOffer>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/reject-offer`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    viewApplicationStage: builder.query<ViewApplicationStage, { applicationId: string, }>({
      query: ({ applicationId }) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/view-application-stage/${applicationId}`,
          method: 'GET',
        })
      }
    }),
    getLoanContract: builder.query<LoanContractResponse, { loanId: string, }>({
      query: ({ loanId }) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/loan-contract/${loanId}`,
          method: 'GET',
        })
      }
    }),
    downloadLoanContract: builder.query<any, { loanId: string, lang?: string, }>({
      query: ({ loanId, lang = 'en' }) => {
        return ({
          url: `${loanOriginationPath}/v2/customer-loans/download-loan-contract/${loanId}`,
          method: 'GET',
          params: { lang },
          responseHandler: (response) => response.text(),
        })
      }
    }),
    getInsuranceContract: builder.query<InsuranceContractResponse, { loanId: string, }>({
      query: ({ loanId }) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/loan-insurance/${loanId}`,
          method: 'GET',
        })
      }
    }),
    signContracts: builder.mutation<BaseResponse, { loanId: string, signature: string, }>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/sign-contract`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    signInsuranceContract: builder.mutation<BaseResponse, { loanApplicationId: string, signature: string, customerUserId: string, }>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/sign-insurance-contract`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    provideAdditionalInfo: builder.mutation<BaseResponse, ProvideAdditionalInfoRequest>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/provide-additional-info`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    getFile: builder.query<{ fileContent: string, }, { fileId: string, }>({
      query: ({ fileId }) => {
        return ({
          url: `${fileManagerPath}/${fileId}`,
          method: 'GET',
        })
      }
    }),
    uploadFile: builder.mutation<UploadFileResponse, UploadFileRequest>({
      query: ({ fileType, ...body }) => {
        return ({
          url: `${fileManagerPath}/upload`,
          method: 'POST',
          params: { fileType },
          body,
        })
      }
    }),
    addRSSBNumber: builder.mutation<GetLoanProfileResponse, { cbsId: string, rssbNumber: string, }>({
      query: ({ cbsId, ...body }) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/${cbsId}/rssb-number`,
          method: 'PATCH',
          body,
        })
      }
    }),
    checkLoanAllowedAmount: builder.mutation<any, PersonalLoanEligibility>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/loans/allowed-amount`,
          method: 'POST',
          body,
        })
      }
    }),
    checkBKStaff: builder.query<any, { accountNumber: string, }>({
      query: ({ accountNumber }) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/${accountNumber}/bk-staff`,
          method: 'GET',
        })
      }
    }),
    signLoanContract: builder.mutation<any, SignLoanContractRequest>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/v2/customer-loans/sign-loan-contract`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    giveCustomerRssbConsent: builder.mutation<any, {
      cbsId: string,
      legalDocument: string,
      legalDocumentType: string,
      rssbDataAccessStatus: string,
    }>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/rssb_consent`,
          method: 'POST',
          body,
        })
      }
    }),
    loadSalary: builder.query<any, { cbsId: string, }>({
      query: ({ cbsId }) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/${cbsId}/load-salary`,
          method: 'GET',
        })
      }
    }),
    fetchSalariesRSSB: builder.query<any, { rssbNumber: string, }>({
      query: ({ rssbNumber }) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-rssb/${rssbNumber}/fetch-salaries`,
          method: 'GET',
        })
      }
    }),
    downloadDocument: builder.query<any, { documentId: string, }>({
      query: ({ documentId }) => {
        return ({
          url: `${loanOriginationPath}/v2/customer-loans/download-document/${documentId}`,
          method: 'GET',
        })
      }
    }),
    rejectLoanApplication: builder.mutation<BaseResponse, { customerUserId: string, loanId: string, loanRejectComment: string, }>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/customer-loans/changed-mind`,
          method: 'POST',
          body,
        })
      },
      invalidatesTags: ['loanApplications'],
    }),
    getAllowedLoans: builder.query<AllowedLoans, { cbsId: string, }>({
      query: ({ cbsId }) => {
        return ({
          url: `${loanOriginationPath}/v2/loans/customer-profile/${cbsId}/allowed-loans`,
          method: 'GET',
        })
      }
    }),
    checkLoanEligibility: builder.mutation<CheckLoanEligibilityResponse, CheckLoanEligibilityRequest>({
      query: (body) => {
        return ({
          url: `${loanOriginationPath}/loans/customer-profile/check-eligibility`,
          method: 'POST',
          body,
        })
      },
    }),
  })

export default loanEndpoints
